import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import 'moment/locale/es';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  SvgIcon,
  makeStyles
} from '@material-ui/core';
import {
  Edit as EditIcon,
  ArrowUp as ArrowUpIcon,
  ArrowDown as ArrowDownIcon,
  Download as DownloadIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon
} from 'react-feather';
import DatatableToolbar from './DatatableToolbar';
import { t, LiteralCode } from '../../../literals';
import { formatNumber } from 'src/shared/support/number';

const useStyles = makeStyles((theme) => ({
  root: {},
  paginationContainer: {
    marginTop: '-60px',
    width: 'auto',
    float: 'right'
  },
  columnCell: {
    cursor: 'pointer'
  },
  editButton: {
    cursor: 'pointer',
    marginRight: '10px'
  },
  orderColumnButton: {
    cursor: 'pointer',
    fontSize: '13px',
    marginLeft: '4px'
  },
  downloadButton: {
    cursor: 'pointer'
  },
  addPaymentButton: {
    cursor: 'pointer',
    marginLeft: '15px'
  },
  closeButton: {
    cursor: 'pointer',
    marginLeft: '15px',
    color: 'green'
  },
  red: {
    background: 'red',
    color: 'white'
  },
  gray: {
    background: 'gray',
    color: 'white'
  },
  yellow: {
    background: 'yellow'
  },
  green: {
    background: 'green',
    color: 'white'
  },
  white: {
    background: 'white'
  },
  orange: {
    background: 'orange',
    color: 'white'
  },
  brown: {
    background: 'brown',
    color: 'white'
  },
  blue: {
    background: 'blue',
    color: 'white'
  },
  black: {
    background: 'black',
    color: 'white'
  }
}));

const Datatable = ({
  className,
  domainName,
  dataList,
  title,
  addButtonRoute,
  addButtonText,
  handleClickAdd,
  importExcelButton,
  exportExcelButton,
  handleClickExportExcel,
  handleClickImportExcel,
  onSearch,
  onChangePageValue,
  handleClickEdit,
  handleClickClose,
  handleClickDownload,
  handleClickAddPayment,
  columnNameList,
  onChangePageLimit,
  rowsPerPage = 10,
  count = 20,
  handleOrderBy,
  pagination = true,
  order = true,
  ...rest
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [limit, setLimit] = useState(rowsPerPage);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState({});

  const getColumnNameI18n = (columnName) => {
    return columnName.indexOf('=') !== -1
      ? t(columnName.split('=')[1])
      : t(columnName);
  };

  const handleSelectAll = (event) => {
    let newSelectedRowIds;

    if (event.target.checked) {
      newSelectedRowIds = dataList.map((row) => row.id);
    } else {
      newSelectedRowIds = [];
    }

    setSelectedRowIds(newSelectedRowIds);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedRowIds.indexOf(id);
    let newSelectedRowIds = [];

    if (selectedIndex === -1) {
      newSelectedRowIds = newSelectedRowIds.concat(selectedRowIds, id);
    } else if (selectedIndex === 0) {
      newSelectedRowIds = newSelectedRowIds.concat(selectedRowIds.slice(1));
    } else if (selectedIndex === selectedRowIds.length - 1) {
      newSelectedRowIds = newSelectedRowIds.concat(selectedRowIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedRowIds = newSelectedRowIds.concat(
        selectedRowIds.slice(0, selectedIndex),
        selectedRowIds.slice(selectedIndex + 1)
      );
    }

    setSelectedRowIds(newSelectedRowIds);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    onChangePageLimit(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);

    onChangePageValue(newPage);
  };

  const handleClickOrderColumn = (columnName) => {
    columnName =
      columnName.indexOf('|') !== -1 ? columnName.split('|')[1] : columnName;

    let orderByRes = 'ASC';
    if (typeof orderBy.columnName !== 'undefined') {
      orderByRes = orderBy.orderBy === 'ASC' ? 'DESC' : 'ASC';
    }

    const orderByItem = {
      columnName,
      orderBy: orderByRes
    };

    const orderByItemFetch = {
      columnName:
        columnName.indexOf('${') !== -1
          ? columnName
              .split('${')[2]
              .replace('${', '')
              .replace('}', '')
          : columnName,
      orderBy: orderByRes
    };

    if (handleOrderBy) {
      handleOrderBy(orderByItemFetch);
    }

    setOrderBy(orderByItem);
  };

  const getRowValue = (row, columnName) => {
    let result = '';

    let link;

    const hasParametersDelimiter = columnName.indexOf(',') !== -1;
    if (hasParametersDelimiter) {
      const [column, parameter] = columnName.split(',');
      if (parameter.indexOf('LINK') !== -1) {
        const [linkey, linkMask] = parameter.split('LINK:');
        const [linkPath, linkItemParamId] = linkMask.split(':');
        const linkItemParamIdValue = row[linkItemParamId.replace(':', '')];
        link = `${linkPath}${linkItemParamIdValue}`;
      }
      columnName = column;
    }

    const hasOrderDelimiterIndex = columnName.indexOf('|');
    if (hasOrderDelimiterIndex !== -1) {
      columnName = columnName.substr(0, hasOrderDelimiterIndex);
    }

    if (columnName.indexOf('{{') !== -1) {
      columnName = columnName.replace('{{', '').replace('}}', '');
      if (columnName.indexOf(`dateFormat(date, 'MMMM')`) !== -1) {
        const date = dataList.filter((x) => x.id === row.id)[0].date;
        result = moment(date)
          .format('MMMM')
          .toUpperCase();
      }
    } else if (columnName.indexOf('${') !== -1) {
      const items = columnName.split('${');

      for (let item of items) {
        item = item.replace('}', '').trim();

        if (item.indexOf('.') !== -1) {
          const splitItem = item.split('.');
          try {
            result += row[splitItem[0]][splitItem[1]] + ' ';
          } catch (e) {
            //console.log(e);
          }
        } else if (item.indexOf("'") !== -1) {
          result += item.replace("'", '').replace("'", '');
        } else if (row[item]) {
          result += row[item];
        }
      }
    } else if (columnName.indexOf('=') !== -1) {
      console.log(columnName);
      const columnNameSplit = columnName.split('=');
      result = row[columnNameSplit[0]];
    } else {
      result = row[columnName];

      if (columnName.indexOf('.') !== -1) {
        const splitColumnName = columnName.split('.');
        if (row[splitColumnName[0]]) {
          row[columnName] = row[splitColumnName[0]][splitColumnName[1]];
        }
      }

      if (
        [
          'date',
          'beginAt',
          'endAt',
          'beginDate',
          'renewalDate',
          'birthdate',
          'endDate',
          'promiseDate',
          'PromisePayment.promiseDate',
          'contactPlannedAt'
        ].includes(columnName)
      ) {
        result = row[columnName]
          ? moment(row[columnName])
              .locale('es')
              .format('DD/MM/YYYY')
          : '';
      } else if (
        [
          'createdAt',
          'updatedAt',
          'datetime',
          'updatedAt',
          'viewedAt',
          'scheduledPaymentDate'
        ].includes(columnName)
      ) {
        result = row[columnName]
          ? moment(row[columnName])
              .locale('es')
              .format('HH:mm:ss DD/MM/YYYY')
          : '';
      } else if (
        row[columnName] &&
        [
          'amount',
          'balance',
          'rest',
          'totalAmount',
          'depositAmount',
          'registeredAmount',
          'registeredDifferenceAmount',
          'invoiceAmount',
          'price',
          'discountPercentage',
          'installationCost',
          'totalPrice'
        ].includes(columnName)
      ) {
        result = formatNumber(row[columnName]);
      } else if (
        typeof row[columnName] === 'string' &&
        typeof t(row[columnName].toLowerCase()) !== 'undefined'
      ) {
        result = t(row[columnName].toLowerCase()).toUpperCase();
      }
    }

    return {
      value: result,
      link
    };
  };

  const getClassNameByValue = (value) => {
    let className = '';

    if (domainName === 'CUSTOMER_TERM' && value === 'PENDING') {
      className = classes.white;
    } else if (
      [
        'PENDING_INVOICE',
        'PENDING_PAYMENT',
        'NO',
        'PENDING',
        'pending',
        'INACTIVE',
        'INACTIVE_REINSTALLATION',
        'INACTIVE_HOLDER_CHANGE',
        'PENDING_DEPOSIT_NOTE',
        '_0_PENDING_CONTACT',
        '_0_ACTIVE_PENDING_CONTACT',
        'INBOUND'
      ].includes(value)
    ) {
      className = classes.red;
    } else if (
      [
        'PENDING_INSTALLATION',
        'ACTIVE_PENDING_PAYMENT',
        'PARTIAL_PAYMENT',
        'PENDING_VALIDATION',
        '_1_CONTACT_PLANNED',
        '_1_ACTIVE_CONTACT_PLANNED'
      ].includes(value)
    ) {
      className = classes.yellow;
    } else if (['INACTIVE_PENDING_INSTALLATION', 'PROMISE'].includes(value)) {
      className = classes.orange;
    } else if (['INACTIVE_WITH_NOTICE', 'CREDIT_NOTE'].includes(value)) {
      className = classes.brown;
    } else if (['SERVICE_CUT'].includes(value)) {
      className = classes.blue;
    } else if (['SERVICE_AND_BILLING_CUT'].includes(value)) {
      className = classes.black;
    } else if (['CANCELLED', 'REJECTED'].includes(value)) {
      className = classes.gray;
    } else if (
      [
        'ACTIVE',
        'holder',
        'PAID',
        'SÍ',
        'OK',
        'VALIDATED',
        'validated',
        'VALIDATED_PAYMENT',
        'OUTBOUND'
      ].includes(value)
    ) {
      className = classes.green;
    }

    return className;
  };

  const handleClickLink = (event, link) => {
    navigate(link, {
      replace: false
    });
  };

  useEffect(() => {
    setSelectedRowIds([0]);
    setSelectedRowIds([]);
  }, [columnNameList]);

  return (
    <>
      <DatatableToolbar
        addButtonRoute={addButtonRoute}
        addButtonText={addButtonText}
        handleAdd={handleClickAdd}
        handleClickExportExcel={handleClickExportExcel}
        handleClickImportExcel={handleClickImportExcel}
        importExcelButton={importExcelButton}
        exportExcelButton={exportExcelButton}
        onSearch={onSearch}
        title={title}
      />
      {pagination ? (
        <TablePagination
          component="div"
          count={count}
          className={classes.paginationContainer}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          labelRowsPerPage={'Filas por página'}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      ) : null}
      <Card className={clsx(classes.root, className)} {...rest}>
        <PerfectScrollbar>
          <Box minWidth={1050}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRowIds.length === dataList.length}
                      color="primary"
                      indeterminate={
                        selectedRowIds.length > 0 &&
                        selectedRowIds.length < dataList.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Opciones</TableCell>
                  {columnNameList.map((columnName) => (
                    <TableCell
                      className={classes.columnCell}
                      onClick={() =>
                        order && handleClickOrderColumn(columnName)
                      }
                    >
                      {getColumnNameI18n(columnName)}
                      {order ? (
                        <SvgIcon
                          className={classes.orderColumnButton}
                          fontSize="small"
                          color="action"
                        >
                          {(orderBy.columnName === columnName &&
                            orderBy.orderBy) === 'ASC' ? (
                            <ArrowUpIcon />
                          ) : (
                            <ArrowDownIcon />
                          )}
                        </SvgIcon>
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataList.length ? (
                  dataList.slice(0, limit).map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      selected={selectedRowIds.indexOf(row.id) !== -1}
                    >
                      {row.id ? (
                        <>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRowIds.indexOf(row.id) !== -1}
                              onChange={(event) =>
                                handleSelectOne(event, row.id)
                              }
                              value="true"
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            <Box display="flex" justifyContent="flex-end">
                              {handleClickEdit ? (
                                <SvgIcon
                                  className={classes.editButton}
                                  onClick={(event) =>
                                    handleClickEdit(event, row.id, row)
                                  }
                                  fontSize="small"
                                  color="action"
                                >
                                  <EditIcon />
                                </SvgIcon>
                              ) : (
                                <></>
                              )}
                              {handleClickDownload &&
                              (!Object.prototype.hasOwnProperty.call(
                                row,
                                'validationStatus'
                              ) ||
                                (Object.prototype.hasOwnProperty.call(
                                  row,
                                  'validationStatus'
                                ) &&
                                  row.validationStatus !== 'pending')) ? (
                                <SvgIcon
                                  className={classes.downloadButton}
                                  onClick={(event) =>
                                    handleClickDownload(event, row.id, row)
                                  }
                                  fontSize="small"
                                  color="action"
                                >
                                  <DownloadIcon />
                                </SvgIcon>
                              ) : (
                                <></>
                              )}
                              {handleClickAddPayment ? (
                                <SvgIcon
                                  className={classes.addPaymentButton}
                                  onClick={(event) =>
                                    handleClickAddPayment(event, row.id)
                                  }
                                  fontSize="small"
                                  color="action"
                                >
                                  <CreditCardIcon />
                                </SvgIcon>
                              ) : (
                                <></>
                              )}
                              {handleClickClose && row.origin === 'INBOUND' ? (
                                <SvgIcon
                                  className={classes.closeButton}
                                  onClick={(event) =>
                                    handleClickClose(event, row.id, row)
                                  }
                                  fontSize="small"
                                >
                                  <CheckCircleIcon />
                                </SvgIcon>
                              ) : (
                                <></>
                              )}
                            </Box>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell padding="checkbox"></TableCell>
                          <TableCell padding="checkbox"></TableCell>
                        </>
                      )}

                      {columnNameList.map((columnName) => {
                        const rowValue = getRowValue(row, columnName);
                        return (
                          <TableCell
                            className={getClassNameByValue(row[columnName])}
                          >
                            {rowValue.link ? (
                              <>
                                <SvgIcon
                                  className={classes.editButton}
                                  onClick={(event) =>
                                    handleClickLink(event, rowValue.link)
                                  }
                                  fontSize="small"
                                  color="action"
                                >
                                  <EditIcon />
                                </SvgIcon>
                                {rowValue.value}
                              </>
                            ) : (
                              rowValue.value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>Sin registros</TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
      </Card>
    </>
  );
};

export default Datatable;
